import { type TRAFFIC_TYPES } from '@/api/common';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Text } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';
import { cn } from '@/lib/utils';

import { type VisitsTableBarCellDataType } from './types';
import VisitsTableBarCellTooltip from './VisitsTableBarCellTooltip';

const ALLOCATED_LABEL_WIDTH = 60;

type Props = {
  data: VisitsTableBarCellDataType[];
  maxVisits?: number;
  displayMode?: 'stack' | 'percent';
  tooltipTitle: string;
  isOpaque: boolean;
  isTooltipOpen: boolean;
  isTooltipSticky: boolean;
  tooltipLink?: (trafficType: TRAFFIC_TYPES) => string | undefined;
  onStickyTooltipClose?: () => void;
};

const VisitsTableBarCell = ({
  data,
  maxVisits = 0,
  displayMode = 'stack',
  tooltipTitle,
  isOpaque = false,
  isTooltipOpen = false,
  isTooltipSticky = false,
  tooltipLink,
  onStickyTooltipClose,
}: Props) => {
  const totalVisits = data.reduce((acc, { value, disabled }) => acc + (disabled ? 0 : value), 0);

  return (
    <div className="my-[-4px] w-full overflow-hidden">
      <div
        className={cn('relative flex', { 'opacity-30': !isOpaque })}
        style={{ width: `calc(100% - ${ALLOCATED_LABEL_WIDTH}px)` }}
      >
        {data.map(({ type, value, color, disabled }) => (
          <div
            key={type}
            className="h-5 transition-[width] duration-500 ease-in-out"
            style={{
              backgroundColor: color,
              width:
                displayMode === 'stack'
                  ? `${disabled ? 0 : (value / maxVisits) * 100}%`
                  : `${disabled ? 0 : (value / totalVisits) * 100}%`,
            }}
          />
        ))}
        <Tooltip open={isTooltipOpen} disableHoverablePopup={!isTooltipSticky}>
          <TooltipTrigger
            render={
              /* A hidden bar with the full width of the stacked series that tooltip can anchor to */
              <div
                className="absolute left-0 h-5"
                style={{
                  width: displayMode === 'stack' ? `${(totalVisits / maxVisits) * 100}%` : '100%',
                }}
              />
            }
          />
          <TooltipContent side="bottom" className="max-w-150 p-5">
            <VisitsTableBarCellTooltip
              data={data}
              totalVisits={totalVisits}
              tooltipTitle={tooltipTitle}
              sticky={isTooltipSticky}
              tooltipLink={tooltipLink}
              onClose={onStickyTooltipClose}
            />
          </TooltipContent>
        </Tooltip>
        <div
          className="absolute ml-2 leading-5 whitespace-nowrap transition-[left] duration-500 ease-in-out select-none"
          style={{
            left: displayMode === 'stack' ? `${(totalVisits / maxVisits) * 100}%` : '100%',
          }}
        >
          {totalVisits > 0 && (
            <Text variant="caption1" className="text-[11px]">
              {numberFormat(totalVisits, { compact: true })}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitsTableBarCell;
